import { FC } from "react";
import { useFragment } from "react-relay";
import { Link, useNavigate } from "react-router-dom";
import { graphql } from "relay-runtime";
import {
  Button,
  Decimal,
  i18n,
  IconDenom,
  formatApr,
  classApr,
} from "rujira.ui";
import { PerpsPoolThumbFragment$key } from "./__generated__/PerpsPoolThumbFragment.graphql";
import { Subscription } from "../../services/useNodeSubscription";

const fragment = graphql`
  fragment PerpsPoolThumbFragment on PerpsAccount {
    id
    lpSize {
      valueUsd
    }
    xlpSize {
      valueUsd
    }
    pool {
      baseAsset {
        metadata {
          symbol
        }
      }
      quoteAsset {
        metadata {
          symbol
        }
      }
      stats {
        lpApr {
          value
          status
        }
      }
    }
  }
`;

const subscription = graphql`
  subscription PerpsPoolThumbSubscription($id: ID!) {
    node(id: $id) {
      ... on PerpsAccount {
        lpSize {
          valueUsd
        }
        xlpSize {
          valueUsd
        }
      }
    }
  }
`;

export const PerpsPoolThumb: FC<{ k: PerpsPoolThumbFragment$key }> = ({
  k,
}) => {
  const data = useFragment(fragment, k);
  const url = `/strategies/derivatives/perps/${data.pool.baseAsset.metadata.symbol}-${data.pool.quoteAsset.metadata.symbol}`;

  return (
    <div className="relative card p-3">
      <Subscription id={data.id} subscription={subscription} />
      <div className="flex ai-c mt-0.5">
        <div className="lp">
          <IconDenom denom={data.pool.baseAsset.metadata.symbol} />
          <IconDenom denom={data.pool.quoteAsset.metadata.symbol} />
        </div>
        <h3 className="condensed fs-24 fw-400 mb-0 ml-1">
          {data.pool.baseAsset.metadata.symbol}{" "}
          <span className="color-grey">/</span>{" "}
          {data.pool.quoteAsset.metadata.symbol}
        </h3>
        <i18n.div className="tag tag--purple ml-a mr-0">
          Perps LP / xLP
        </i18n.div>
      </div>
      <div className="row wrap pad--mini mt-2">
        <div className="col-4">
          <i18n.h4 className="fs-16 lh-22 fw-400 color-grey mb-0">
            Value
          </i18n.h4>

          <Decimal
            symbol="$"
            symbolLeft
            amount={
              BigInt(data.lpSize.valueUsd) + BigInt(data.xlpSize.valueUsd)
            }
            decimals={8}
            round={2}
            className="fs-24 condensed"
          />
        </div>
      </div>
      <Button
        as={Link}
        to={url}
        className="button--small button--outline mt-2 w-full"
        label="Manage Position"
      />
    </div>
  );
};

export const PerpsPoolRow: FC<{ k: PerpsPoolThumbFragment$key }> = ({ k }) => {
  const data = useFragment(fragment, k);
  const url = `/strategies/derivatives/perps/${data.pool.baseAsset.metadata.symbol}-${data.pool.quoteAsset.metadata.symbol}`;
  const navigate = useNavigate();

  return (
    <tr onClick={() => navigate(url)} className="pointer">
      <Subscription id={data.id} subscription={subscription} />
      <td className="flex ai-c mt-0.5 pointer">
        <div className="lp">
          <IconDenom denom={data.pool.baseAsset.metadata.symbol} />
          <IconDenom denom={data.pool.quoteAsset.metadata.symbol} />
        </div>
      </td>
      <td style={{ paddingLeft: "0" }}>
        {data.pool.baseAsset.metadata.symbol}{" "}
        <span className="color-grey">/</span>{" "}
        {data.pool.quoteAsset.metadata.symbol}
      </td>
      <td>
        <i18n.div className="tag tag--purple ml-a mr-0">
          Perps LP / xLP
        </i18n.div>
      </td>
      <td>
        <Decimal
          symbol="LP"
          amount={BigInt(data.lpSize.valueUsd)}
          round={4}
          symbolClassName="decimal__symbol--small color-grey condensed fw-500 mr-1"
        />
        <Decimal
          symbol="xLP"
          amount={BigInt(data.xlpSize.valueUsd)}
          round={4}
          symbolClassName="decimal__symbol--small color-grey condensed fw-500 mr-1"
        />
      </td>
      <td className="text-right">
        <span className={classApr(data.pool.stats.lpApr)}>
          {formatApr(data.pool.stats.lpApr)}
        </span>
      </td>
      <td className="text-right">
        <Decimal
          symbol="$"
          symbolLeft
          amount={BigInt(data.lpSize.valueUsd) + BigInt(data.xlpSize.valueUsd)}
          decimals={8}
          round={2}
        />
      </td>
    </tr>
  );
};
